import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DEFAULT_STATUSES } from '../../FlowchartView/TaskNode/constants';
import { toLegacyStatusConfig } from '../../FlowchartView/utils/statusUtils';
import { commitAfterPaint, hideMenuNow } from './hooks/paintChoice';
import ConfirmModal from '../../FlowchartView/components/ConfirmModal';
import DependencyWarningModal from '../DependencyWarningModal';
import DeliverablesWarningModal from '../DeliverablesWarningModal';
import NestedCampaignWarningModal from '../NestedCampaignWarningModal';
import StatusTrigger from './components/StatusTrigger';
import StatusOptionsList from './components/StatusOptionsList';
import AddStatusForm from './components/AddStatusForm';
import { useMenuPosition } from './hooks/useMenuPosition';

// Styles
import './styles/index.css';

/**
 * StatusDropdown Component (Shared)
 * 
 * A unified status dropdown component used across all views:
 * - Flow view
 * - Kanban view
 * - Table view
 * - TodoList view
 * 
 * Features:
 * - Multiple display variants: 'pill' (compact), 'button' (full), 'badge' (minimal)
 * - Add/rename/delete statuses
 * - Locked state handling
 * - Nested sprint auto-sync support
 * - Fixed width with text truncation for consistency
 * 
 * @param {string} variant - Display style: 'pill' | 'button' | 'badge' (default: 'pill')
 * @param {string} taskId - The task ID
 * @param {string} currentStatus - Current status ID
 * @param {boolean} isLocked - Whether the task is locked by dependencies
 * @param {string} lockReason - Reason for lock
 * @param {Array} blockingDependencies - Array of blocking dependencies { id, name }
 * @param {boolean} hasNestedSprint - Whether task has a nested sprint
 * @param {number} nestedSprintProgress - Progress percentage of nested sprint
 * @param {boolean} statusAutoSync - Whether status auto-syncs with nested sprint
 * @param {function} onStatusChange - Callback when status changes
 * @param {function} onSetStatusAutoSync - Callback to toggle auto-sync
 * @param {Array} statuses - Available statuses
 * @param {Array} allTasks - All tasks in the campaign (used for delete warnings)
 * @param {function} onAddStatus - Callback to add a status
 * @param {function} onDeleteStatus - Callback to delete a status
 * @param {function} onRenameStatus - Callback to rename a status
 * @param {boolean} showLabel - Whether to show "Status" label (for button variant)
 * @param {string} className - Additional CSS class
 * @param {function} onGoToDependency - Optional callback(depId) when user clicks a dependency in warning modal
 */
const StatusDropdown = ({
  variant = 'pill',
  taskId,
  taskName = '',
  currentStatus,
  isLocked = false,
  lockReason,
  blockingDependencies = [],
  hasNestedSprint = false,
  nestedSprintProgress = 0,
  statusAutoSync = true,
  onStatusChange,
  onSetStatusAutoSync,
  statuses = DEFAULT_STATUSES,
  allTasks = [],
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  showLabel = false,
  className = '',
  onGoToDependency,
  /** When set (parent mission locked), DependencyWarningModal uses nested-campaign copy */
  parentMissionName,
  parentCampaignName,
  onGoToParentMission,
  // Deliverables warning support
  deliverables = [],
  onSubmitDeliverable,
  onOpenDetails,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // { statusId, statusName } for locked warning
  const [pendingNestedOverride, setPendingNestedOverride] = useState(null); // { statusId, statusName } for nested campaign override warning
  const [pendingDeliverablesWarning, setPendingDeliverablesWarning] = useState(null); // { statusId, statusName } for deliverables warning
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  
  // Calculate incomplete deliverables
  const incompleteDeliverables = deliverables.filter(d => !d.completed);
  
  // Unique ID for this dropdown instance
  const dropdownId = useRef(`status-${taskId}`).current;

  // Close menu handler
  const closeMenu = useCallback(() => {
    setShowMenu(false);
    setShowAddForm(false);
  }, []);

  // Menu positioning hook (with scroll-to-close)
  const { menuPosition, openMenu, showNow, commitPosition, repositionMenu } = useMenuPosition(btnRef, menuRef, showMenu, closeMenu);
  const openedOnPress = useRef(false);

  // Close when another dropdown opens (only listen when this menu is open)
  useEffect(() => {
    if (!showMenu) return;
    
    const handleCloseDropdowns = (e) => {
      if (e.detail?.exceptId !== dropdownId) {
        closeMenu();
      }
    };
    
    window.addEventListener('closeTableDropdowns', handleCloseDropdowns);
    return () => window.removeEventListener('closeTableDropdowns', handleCloseDropdowns);
  }, [showMenu, dropdownId, closeMenu]);

  // If task has nested sprint AND auto-sync is enabled, show auto-sync indicator
  const isAutoSyncEnabled = hasNestedSprint && statusAutoSync !== false;

  // Close menu when clicking outside
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setShowMenu(false);
      setShowAddForm(false);
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showMenu]);

  // Close when canvas starts panning (trackpad or mouse drag)
  useEffect(() => {
    if (!showMenu) return;

    const handlePanStart = () => closeMenu();
    window.addEventListener('flowchartpanstart', handlePanStart);
    return () => window.removeEventListener('flowchartpanstart', handlePanStart);
  }, [showMenu, closeMenu]);

  // The add form changes the menu's height. Nudge it after that paint, not on open.
  useEffect(() => {
    if (!showAddForm) return undefined;
    const frame = requestAnimationFrame(() => repositionMenu());
    return () => cancelAnimationFrame(frame);
  }, [showAddForm, repositionMenu]);

  const rowCount = (statuses?.length || 0) + (onAddStatus ? 1 : 0) + (hasNestedSprint ? 1 : 0);

  // Reveal on the press. Building the menu in React happens after the first paint.
  const handleMouseDown = (e) => {
    if (showMenu) return;
    const pos = showNow(rowCount, e);
    openedOnPress.current = true;
    window.dispatchEvent(new CustomEvent('closeTableDropdowns', { detail: { exceptId: dropdownId } }));
    setTimeout(() => {
      commitPosition(pos);
      setShowMenu(true);
    }, 0);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (openedOnPress.current) {
      openedOnPress.current = false;
      return;
    }
    if (!showMenu) {
      window.dispatchEvent(new CustomEvent('closeTableDropdowns', { detail: { exceptId: dropdownId } }));
      openMenu(rowCount, e);
      setShowMenu(true);
      return;
    }
    setShowMenu(false);
    setShowAddForm(false);
  };

  const handleSelect = useCallback((statusId) => {
    const selectedStatus = statuses.find(s => s.id === statusId);
    const isNotStartedCategory = selectedStatus?.category === 'not_started';
    const isCompletedCategory = selectedStatus?.category === 'completed';
    hideMenuNow(menuRef.current);

    if (isLocked && !isNotStartedCategory) {
      commitAfterPaint(() => {
        setPendingStatusChange({
          statusId,
          statusName: selectedStatus?.name || statusId,
          statusColor: selectedStatus?.color || '',
        });
        setShowMenu(false);
      });
      return;
    }

    if (isCompletedCategory && incompleteDeliverables.length > 0) {
      commitAfterPaint(() => {
        setPendingDeliverablesWarning({ statusId, statusName: selectedStatus?.name || statusId });
        setShowMenu(false);
      });
      return;
    }

    if (hasNestedSprint && statusAutoSync !== false && onSetStatusAutoSync) {
      commitAfterPaint(() => {
        setPendingNestedOverride({ statusId, statusName: selectedStatus?.name || statusId });
        setShowMenu(false);
      });
      return;
    }

    const button = btnRef.current;
    const painted = toLegacyStatusConfig(selectedStatus);
    if (button && selectedStatus) {
      button.style.setProperty('--status-color', painted.color);
      button.style.setProperty('--status-bg', painted.bg);
      const label = button.querySelector('.status-dropdown__trigger-label, .status-dropdown__trigger-text');
      if (label) label.textContent = painted.label;
    }
    commitAfterPaint(() => {
      onStatusChange?.(taskId, statusId);
      setShowMenu(false);
    });
  }, [isLocked, onStatusChange, taskId, hasNestedSprint, statusAutoSync, onSetStatusAutoSync, statuses, incompleteDeliverables]);

  const handleSelectAuto = useCallback(() => {
    hideMenuNow(menuRef.current);
    if (isLocked && nestedSprintProgress === 100) {
      commitAfterPaint(() => {
        setPendingStatusChange({ statusId: '__auto__', statusName: 'Auto (synced with campaign)' });
        setShowMenu(false);
      });
      return;
    }
    commitAfterPaint(() => {
      onSetStatusAutoSync?.(taskId, true);
      setShowMenu(false);
    });
  }, [isLocked, onSetStatusAutoSync, taskId, nestedSprintProgress]);

  // Handle confirming a status change despite lock warning
  const handleConfirmLockedStatusChange = useCallback(() => {
    if (!pendingStatusChange) return;
    
    if (pendingStatusChange.statusId === '__auto__') {
      onSetStatusAutoSync?.(taskId, true);
    } else if (hasNestedSprint && statusAutoSync !== false && onSetStatusAutoSync) {
      onSetStatusAutoSync(taskId, false, pendingStatusChange.statusId);
    } else {
      onStatusChange?.(taskId, pendingStatusChange.statusId);
    }
    setPendingStatusChange(null);
  }, [pendingStatusChange, taskId, hasNestedSprint, statusAutoSync, onSetStatusAutoSync, onStatusChange]);

  // Handle confirming a nested campaign override (disable auto-sync and set status)
  const handleConfirmNestedOverride = useCallback(() => {
    if (!pendingNestedOverride) return;
    
    // Disable auto-sync and set the new status atomically
    onSetStatusAutoSync?.(taskId, false, pendingNestedOverride.statusId);
    setPendingNestedOverride(null);
  }, [pendingNestedOverride, taskId, onSetStatusAutoSync]);

  // Handle confirming a status change despite incomplete deliverables
  const handleConfirmDeliverablesWarning = useCallback(() => {
    if (!pendingDeliverablesWarning) return;
    
    onStatusChange?.(taskId, pendingDeliverablesWarning.statusId);
    setPendingDeliverablesWarning(null);
  }, [pendingDeliverablesWarning, taskId, onStatusChange]);

  const handleAddStatus = useCallback((newStatus) => {
    onAddStatus?.(newStatus);
    setShowAddForm(false);
  }, [onAddStatus]);

  const handleRequestDelete = useCallback((statusId, statusName, errorReason) => {
    if (errorReason) {
      setDeleteError({ statusName, reason: errorReason });
    } else {
      // Count tasks using this status
      const tasksUsingStatus = allTasks.filter(task => task.status === statusId);
      setDeleteConfirm({ statusId, statusName, taskCount: tasksUsingStatus.length });
    }
  }, [allTasks]);

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirm) {
      onDeleteStatus?.(deleteConfirm.statusId);
      setDeleteConfirm(null);
      setShowMenu(false);
    }
  }, [deleteConfirm, onDeleteStatus]);

  // Render menu content
  // Note: We now show statuses even when locked - the warning modal handles blocked status changes
  const renderMenuContent = () => {
    if (showAddForm) {
      return (
        <AddStatusForm
          statuses={statuses}
          onAddStatus={handleAddStatus}
          onCancel={() => setShowAddForm(false)}
        />
      );
    }

    return (
      <StatusOptionsList
        statuses={statuses}
        currentStatus={currentStatus}
        onSelect={handleSelect}
        onAddStatus={onAddStatus}
        onDeleteStatus={onDeleteStatus}
        onRenameStatus={onRenameStatus}
        onShowAddForm={() => setShowAddForm(true)}
        onRequestDelete={handleRequestDelete}
        hasNestedSprint={hasNestedSprint}
        isAutoSyncEnabled={isAutoSyncEnabled}
        nestedSprintProgress={nestedSprintProgress}
        onSelectAuto={handleSelectAuto}
        isLocked={isLocked}
      />
    );
  };

  const menuContent = (
    <div 
      ref={menuRef}
      className={`status-dropdown__menu${showMenu ? '' : ' status-dropdown__menu--shut'}${isLocked ? ' status-dropdown__menu--locked' : ''}`}
      style={{ 
        position: 'fixed',
        top: menuPosition.top, 
        left: menuPosition.left,
        zIndex: 10000,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {renderMenuContent()}
    </div>
  );

  return (
    <>
      <StatusTrigger
        ref={btnRef}
        variant={variant}
        currentStatus={currentStatus}
        statuses={statuses}
        isLocked={isLocked}
        lockReason={lockReason}
        hasNestedSprint={hasNestedSprint}
        nestedSprintProgress={nestedSprintProgress}
        statusAutoSync={statusAutoSync}
        showLabel={showLabel}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        className={className}
      />

      {createPortal(menuContent, document.body)}
      
      <ConfirmModal
        isOpen={!!deleteConfirm}
        title={deleteConfirm?.taskCount > 0 ? "Delete Status with Tasks" : "Delete Status"}
        message={
          deleteConfirm?.taskCount > 0
            ? `⚠️ ${deleteConfirm.taskCount} task${deleteConfirm.taskCount === 1 ? ' is' : 's are'} currently using "${deleteConfirm?.statusName}".\n\nIf you delete this status, ${deleteConfirm.taskCount === 1 ? 'this task' : 'these tasks'} will be moved to a default status.`
            : `Are you sure you want to delete "${deleteConfirm?.statusName}"?`
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant={deleteConfirm?.taskCount > 0 ? "warning" : "danger"}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
      
      <ConfirmModal
        isOpen={!!deleteError}
        title="Cannot Delete Status"
        message={`"${deleteError?.statusName}" cannot be deleted.\n\n${deleteError?.reason}`}
        confirmLabel="OK"
        confirmVariant="primary"
        onConfirm={() => setDeleteError(null)}
        onCancel={() => setDeleteError(null)}
      />

      {/* Warning modal when trying to change status on a locked task */}
      <DependencyWarningModal
        isOpen={!!pendingStatusChange}
        blockingDependencies={blockingDependencies}
        actionLabel={`change this to "${pendingStatusChange?.statusName}"`}
        missionName={taskName}
        targetStatusName={pendingStatusChange?.statusName}
        targetStatusColor={pendingStatusChange?.statusColor}
        statuses={statuses}
        onConfirm={handleConfirmLockedStatusChange}
        onCancel={() => setPendingStatusChange(null)}
        onGoToDependency={onGoToDependency}
        parentMissionName={parentMissionName}
        parentCampaignName={parentCampaignName}
        onGoToParentMission={onGoToParentMission}
      />

      {/* Warning modal when trying to override auto-sync on a nested campaign */}
      <NestedCampaignWarningModal
        isOpen={!!pendingNestedOverride}
        nestedSprintProgress={nestedSprintProgress}
        targetStatusName={pendingNestedOverride?.statusName || ''}
        onConfirm={handleConfirmNestedOverride}
        onCancel={() => setPendingNestedOverride(null)}
      />

      {/* Warning modal when trying to complete with incomplete deliverables */}
      <DeliverablesWarningModal
        isOpen={!!pendingDeliverablesWarning}
        incompleteDeliverables={incompleteDeliverables}
        totalDeliverables={deliverables.length}
        taskName={taskName}
        onConfirm={handleConfirmDeliverablesWarning}
        onCancel={() => setPendingDeliverablesWarning(null)}
        onSubmitDeliverable={onSubmitDeliverable ? (deliverable) => onSubmitDeliverable(taskId, taskName, deliverable) : null}
        onOpenMissionView={onOpenDetails ? () => onOpenDetails(taskId) : null}
      />
    </>
  );
};

export default StatusDropdown;
